import httpClient from "../../../shared/api/httpClient";

function query(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.append(key, String(value));
  });
  const value = search.toString();
  return value ? `?${value}` : "";
}

function resolveLicenseId(explicitLicenseId) {
  if (explicitLicenseId) return String(explicitLicenseId);
  try {
    return localStorage.getItem("sabi_empresa_id") || "";
  } catch {
    return "";
  }
}

function unwrap(response) {
  return response?.data ?? response;
}

function unwrapList(response) {
  const payload = unwrap(response);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

function mapMovementCategory(raw = {}) {
  return {
    ...raw,
    movement_type:
      raw.movementTypeScope ?? raw.movement_type ?? raw.type ?? "",
    is_active: raw.isActive ?? raw.is_active ?? true,
    deleted_at: raw.deletedAt ?? raw.deleted_at ?? null,
  };
}

function mapAccount(raw = {}) {
  return {
    ...raw,
    account_type: raw.accountType ?? raw.account_type ?? raw.type ?? "",
    currency: raw.currency ?? "ARS",
    is_active: raw.isActive ?? raw.is_active ?? true,
    deleted_at: raw.deletedAt ?? raw.deleted_at ?? null,
  };
}

function mapSettings(raw = {}) {
  return {
    ...raw,
    default_movement_type:
      raw.defaultMovementType ?? raw.default_movement_type ?? "INCOME",
  };
}

function serializeMovementCategory(data = {}) {
  return {
    name: String(data.name || "").trim(),
    movementTypeScope:
      data.movementTypeScope ?? data.movement_type ?? data.type,
  };
}

function serializeAccount(data = {}) {
  return {
    name: String(data.name || "").trim(),
    accountType: data.accountType ?? data.account_type ?? data.type,
    currency: "ARS",
  };
}

function serializeSettings(data = {}) {
  return {
    defaultMovementType:
      data.defaultMovementType ?? data.default_movement_type,
  };
}

export const TreasuryApi = {
  async listMovementCategories() {
    const response = await httpClient.get(
      `/treasury/movement-categories${query({ is_active: true })}`
    );
    return unwrapList(response).map(mapMovementCategory);
  },

  async getMovementCategory(id, { includeDeleted = false } = {}) {
    const response = await httpClient.get(
      `/treasury/movement-categories/${id}${query({
        include_deleted: includeDeleted || undefined,
      })}`
    );
    return mapMovementCategory(unwrap(response));
  },

  async createMovementCategory(data) {
    const response = await httpClient.post(
      "/treasury/movement-categories",
      serializeMovementCategory(data)
    );
    return mapMovementCategory(unwrap(response));
  },

  async updateMovementCategory(id, data) {
    const response = await httpClient.patch(
      `/treasury/movement-categories/${id}`,
      serializeMovementCategory(data)
    );
    return mapMovementCategory(unwrap(response));
  },

  deleteMovementCategory(id) {
    return httpClient.delete(`/treasury/movement-categories/${id}`);
  },

  async listAccounts() {
    const response = await httpClient.get("/treasury/accounts");
    return unwrapList(response).map(mapAccount);
  },

  async getAccount(id) {
    const response = await httpClient.get(`/treasury/accounts/${id}`);
    return mapAccount(unwrap(response));
  },

  async getAccountHistory(id) {
    const response = await httpClient.get(`/treasury/accounts/${id}/history`);
    return mapAccount(unwrap(response));
  },

  async createAccount(data) {
    const response = await httpClient.post(
      "/treasury/accounts",
      serializeAccount(data)
    );
    return mapAccount(unwrap(response));
  },

  async updateAccount(id, data) {
    const response = await httpClient.patch(
      `/treasury/accounts/${id}`,
      serializeAccount(data)
    );
    return mapAccount(unwrap(response));
  },

  deleteAccount(id) {
    return httpClient.delete(`/treasury/accounts/${id}`);
  },

  async getSettings({ licenseId } = {}) {
    const activeLicenseId = resolveLicenseId(licenseId);
    const response = await httpClient.get(
      `/admin/licenses/${activeLicenseId}/treasury/settings`
    );
    return mapSettings(unwrap(response));
  },

  async updateSettings(data, { licenseId } = {}) {
    const activeLicenseId = resolveLicenseId(licenseId);
    const response = await httpClient.patch(
      `/admin/licenses/${activeLicenseId}/treasury/settings/default-movement-type`,
      serializeSettings(data)
    );
    return mapSettings(unwrap(response));
  },
};

export default TreasuryApi;
